// Chart.js visuals for the tenant email statistics dashboard (ops + people).
// Labels/counts are server-rendered (already translated) via Stimulus values.

import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  DoughnutController,
  BarElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
} from "chart.js"

Chart.register(
  BarController,
  DoughnutController,
  BarElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip
)

const COLORS = {
  blue: "rgba(59, 130, 246, 0.85)",
  green: "rgba(16, 185, 129, 0.85)",
  amber: "rgba(245, 158, 11, 0.85)",
  red: "rgba(239, 68, 68, 0.85)",
  purple: "rgba(139, 92, 246, 0.85)",
  pink: "rgba(236, 72, 153, 0.85)",
  teal: "rgba(20, 184, 166, 0.85)",
  gray: "rgba(107, 114, 128, 0.85)",
  cyan: "rgba(6, 182, 212, 0.85)",
  softGray: "rgba(229, 231, 235, 0.95)",
}

const DELIVERY_COLORS = {
  delivered: COLORS.green,
  bounced: COLORS.red,
  complained: COLORS.amber,
  rejected: COLORS.purple,
  delayed: COLORS.cyan,
  sent: COLORS.blue,
  pending: COLORS.gray,
  failed: COLORS.red,
}

const STATUS_COLORS = {
  open: COLORS.blue,
  inprogress: COLORS.amber,
  waiting_for_customer: COLORS.cyan,
  closed: COLORS.green,
}

const RESPONSE_COLORS = [
  COLORS.green,
  COLORS.blue,
  COLORS.cyan,
  COLORS.amber,
  COLORS.red,
]

/** Plugin: draw a percentage string in the doughnut hole. */
function centerTextPlugin(text, color = "#111827") {
  return {
    id: `centerText-${text}`,
    afterDraw(chart) {
      const { ctx, chartArea } = chart
      if (!chartArea) return
      const cx = (chartArea.left + chartArea.right) / 2
      const cy = (chartArea.top + chartArea.bottom) / 2
      ctx.save()
      ctx.font = "600 1.1rem system-ui, sans-serif"
      ctx.fillStyle = color
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(text, cx, cy)
      ctx.restore()
    },
  }
}

export default class extends Controller {
  static targets = [
    "delivery",
    "engagementOpen",
    "engagementClick",
    "kind",
    "responsible",
    "status",
    "responseTime",
  ]

  static values = {
    deliveryLabels: Array,
    deliveryCounts: Array,
    deliveryKeys: Array,
    openRate: Number,
    clickRate: Number,
    openLabel: String,
    clickLabel: String,
    restLabel: String,
    kindLabels: Array,
    kindInbound: Array,
    kindOutbound: Array,
    inboundLabel: String,
    outboundLabel: String,
    responsibleLabels: Array,
    responsibleOpen: Array,
    responsibleClosed: Array,
    responsibleOther: Array,
    openStatusLabel: String,
    closedStatusLabel: String,
    otherStatusLabel: String,
    statusLabels: Array,
    statusCounts: Array,
    statusKeys: Array,
    responseLabels: Array,
    responseCounts: Array,
  }

  connect() {
    this.charts = []
    this._renderDelivery()
    this._renderEngagement()
    this._renderKind()
    this._renderResponsible()
    this._renderStatus()
    this._renderResponseTime()
  }

  disconnect() {
    this.charts.forEach((c) => c.destroy())
    this.charts = []
  }

  _push(chart) {
    this.charts.push(chart)
  }

  _hasData(arr) {
    return Array.isArray(arr) && arr.some((n) => Number(n) > 0)
  }

  _renderDelivery() {
    if (!this.hasDeliveryTarget) return
    const labels = this.deliveryLabelsValue || []
    const counts = this.deliveryCountsValue || []
    const keys = this.deliveryKeysValue || []
    if (!this._hasData(counts)) return

    this._push(
      new Chart(this.deliveryTarget, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: counts,
              backgroundColor: keys.map((k) => DELIVERY_COLORS[k] || COLORS.gray),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: { boxWidth: 12, padding: 10 },
            },
          },
        },
      })
    )
  }

  _renderEngagement() {
    this._renderRateDonut(
      this.hasEngagementOpenTarget ? this.engagementOpenTarget : null,
      this.openRateValue,
      this.openLabelValue,
      COLORS.blue
    )
    this._renderRateDonut(
      this.hasEngagementClickTarget ? this.engagementClickTarget : null,
      this.clickRateValue,
      this.clickLabelValue,
      COLORS.cyan
    )
  }

  _renderRateDonut(canvas, rate, label, color) {
    if (!canvas) return
    const pct = Math.max(0, Math.min(100, Math.round((Number(rate) || 0) * 1000) / 10))
    const rest = Math.max(0, 100 - pct)

    this._push(
      new Chart(canvas, {
        type: "doughnut",
        data: {
          labels: [label || "", this.restLabelValue || ""],
          datasets: [
            {
              data: [pct, rest],
              backgroundColor: [color, COLORS.softGray],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.label}: ${ctx.raw}%`,
              },
            },
          },
        },
        plugins: [centerTextPlugin(`${pct}%`, color.replace("0.85", "1"))],
      })
    )
  }

  _renderKind() {
    if (!this.hasKindTarget) return
    const labels = this.kindLabelsValue || []
    if (!labels.length) return
    if (!this._hasData(this.kindInboundValue) && !this._hasData(this.kindOutboundValue)) return

    this._push(
      new Chart(this.kindTarget, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: this.inboundLabelValue,
              data: this.kindInboundValue,
              backgroundColor: COLORS.blue,
              borderRadius: 4,
              stack: "traffic",
            },
            {
              label: this.outboundLabelValue,
              data: this.kindOutboundValue,
              backgroundColor: COLORS.green,
              borderRadius: 4,
              stack: "traffic",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
          scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      })
    )
  }

  _renderResponsible() {
    if (!this.hasResponsibleTarget) return
    const labels = this.responsibleLabelsValue || []
    if (!labels.length) return

    this._push(
      new Chart(this.responsibleTarget, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: this.openStatusLabelValue,
              data: this.responsibleOpenValue,
              backgroundColor: COLORS.amber,
              borderRadius: 3,
              stack: "load",
            },
            {
              label: this.closedStatusLabelValue,
              data: this.responsibleClosedValue,
              backgroundColor: COLORS.green,
              borderRadius: 3,
              stack: "load",
            },
            {
              label: this.otherStatusLabelValue,
              data: this.responsibleOtherValue,
              backgroundColor: COLORS.gray,
              borderRadius: 3,
              stack: "load",
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
          scales: {
            x: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
            y: { stacked: true },
          },
        },
      })
    )
  }

  _renderStatus() {
    if (!this.hasStatusTarget) return
    const labels = this.statusLabelsValue || []
    const counts = this.statusCountsValue || []
    const keys = this.statusKeysValue || []
    if (!this._hasData(counts)) return

    this._push(
      new Chart(this.statusTarget, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: counts,
              backgroundColor: keys.map((k) => STATUS_COLORS[k] || COLORS.gray),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: { boxWidth: 12, padding: 10 },
            },
          },
        },
      })
    )
  }

  _renderResponseTime() {
    if (!this.hasResponseTimeTarget) return
    const labels = this.responseLabelsValue || []
    const counts = this.responseCountsValue || []
    if (!labels.length || !this._hasData(counts)) return

    this._push(
      new Chart(this.responseTimeTarget, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              data: counts,
              backgroundColor: labels.map((_l, i) => RESPONSE_COLORS[i % RESPONSE_COLORS.length]),
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      })
    )
  }
}
